import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/services/api/apiRequest";

/**
 * Invitations to steward a legacy — the only way onto one somebody else filed.
 *
 * Naming an estate used to be enough, which made the picker a way to browse
 * the directory and attach yourself to anything in it. Now a steward asks you,
 * and accepting is what makes the link.
 */
export type LegacyInvite = {
  id: number;
  status: "pending" | "accepted" | "revoked";
  /** The address it was sent to — which is what the recipient must be signed
   *  in as, so it is shown on both sides. */
  email: string;
  created_at: string;
  organization: { id: number; name: string; image_url: string | null };
  invited_by: string;
  /** Only on the recipient's copy: whether the inviter's paid membership
   *  carries over. Read when the invitation is accepted, not when it was sent. */
  grants_membership?: boolean;
};

export type MyInvites = { received: LegacyInvite[]; sent: LegacyInvite[] };

const INVITES = ["afell", "invites"];

/** Both sides at once: what has been offered to me, and what I am waiting on. */
export function useMyInvites(enabled = true) {
  return useQuery<MyInvites>({
    queryKey: INVITES,
    queryFn: () => apiRequest("/afell/invites/"),
    enabled,
  });
}

function useInviteMutation<TVars, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  { touchesMembership = false } = {}
) {
  const queryClient = useQueryClient();
  return useMutation<TResult, Error, TVars>({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITES });
      if (touchesMembership) {
        // Accepting can hand over the inviter's coverage and always adds a
        // legacy, so the shell, the profile and the directory all go stale.
        queryClient.invalidateQueries({ queryKey: ["afell", "me"] });
        queryClient.invalidateQueries({ queryKey: ["afell", "organizations"] });
        queryClient.invalidateQueries({ queryKey: ["afell", "directory"] });
      }
    },
  });
}

/** Ask somebody to steward a legacy you steward. */
export function useSendInvite() {
  return useInviteMutation<{ organizationId: number; email: string }>(
    ({ organizationId, email }) =>
      apiRequest(`/afell/organizations/${organizationId}/invite/`, "POST", { email })
  );
}

/** Accept the one in the emailed link, by its token.
 *
 * What you are to the artist comes with it: relationship is a property of the
 * link, so joining without one makes the only kind of link that cannot answer
 * the question, and the registration form used to carry that debt. */
export function useAcceptInviteToken() {
  return useInviteMutation<{ token: string; relationship: string[] }, AcceptedInvite>(
    (body) => apiRequest("/afell/invites/accept/", "POST", body),
    { touchesMembership: true }
  );
}

/** What accepting answers: which legacy, and whether it carried a membership. */
export type AcceptedInvite = { organization: string; granted_membership: boolean };

/**
 * The three answers given from the page: accept, turn it down, or withdraw one
 * you sent. Accepting here rather than from the email is for anybody who
 * deleted it — the address is the check either way.
 */
export function useRespondToInvite() {
  return useInviteMutation<
    {
      id: number;
      action: "accept" | "decline" | "revoke";
      relationship?: string[];
    },
    AcceptedInvite | { id: number; status: string }
  >(
    ({ id, action, relationship }) =>
      apiRequest(`/afell/invites/${id}/respond/`, "POST", { action, relationship }),
    { touchesMembership: true }
  );
}
